import Vue from 'vue';
import { mapGetters } from 'vuex';
import Component from 'vue-class-component';
import * as Constants from '@/constants';
import { Inject, Watch } from 'vue-property-decorator';
import { maxLength, required } from 'vuelidate/lib/validators';
import EntityFactory from '@/shared/entity-commons/entity.factory';
import AlertService from '@/shared/alert/alert.service';
import { ReniecytApi } from '@/shared/openapi-reniecyt-client/api';

const VALIDATIONS = function () {
  return {
    comentarioTemporal: {
      comentario: {
        required,
        maxLength: maxLength(4200),
      },
    },
  };
};
@Component({
  validations: VALIDATIONS,
  computed: {
    ...mapGetters(['authenticated', 'selectedRole']),
  },
})
export default class ComentarioComponent extends Vue {
  @Inject('alertService') private alertService: () => AlertService;
  @Inject('reniecytApi') private reniecytApi: () => ReniecytApi;

  public comentarioTemporal = EntityFactory.getComentarioTemporal();
  private comentarioTemporalBase = EntityFactory.saveComentarioTemporal();

  public authenticated;
  public isCommentDialogActivated = false;
  public comentario = '';
  public selectedRole;
  public roleOperativoReniecyt = Constants.ROLE_OPERATIVO_RENIECYT;
  public routeName = null;
  public comentarioExistente = null;
  public isRepresentante = false;
  public isSubSede = false;

  toggleComentario() {
    this.isCommentDialogActivated = !this.isCommentDialogActivated;
    if (this.isCommentDialogActivated) {
      if (this.$store.getters.idRegistro && this.$store.getters.idRegistroEstatus) {
        this.consultaComentarioTemporal();
      } else {
        const registroStr = localStorage.getItem('itemRegistroComentario');

        if (registroStr) {
          const registro = JSON.parse(window.atob(registroStr));
          this.$store.commit('setDatosComentario', registro);
          this.consultaComentarioTemporal();
        } else {
          this.alertService().showWarning(this, this.$t('comentario-temporal.alert.sin-datos-consulta').toString());
        }
      }
    }
    this.comentarioTemporal = EntityFactory.getComentarioTemporal();
    this.$v.comentarioTemporal.$reset();
  }

  @Watch('$route', { immediate: true, deep: true })
  onRouteChange(newVal, oldVal) {
    this.routeName = this.$route.name;

    this.isRepresentante = EntityFactory.obtenerIsRepresentante();
    this.isSubSede = EntityFactory.obtenerIsSubSede();

    //En caso de que de retoceso a la pagina principal del revisor y no haya cerrado el modal
    if (this.routeName == 'ConsultaRegistros') {
      this.isCommentDialogActivated = false;
      this.comentarioTemporal = EntityFactory.getComentarioTemporal();
      this.$v.comentarioTemporal.$reset();
    }
  }

  public guardaComentario() {
    if (!this.$v.comentarioTemporal.$invalid) {
      if (this.$store.getters.idRegistro && this.$store.getters.idRegistroEstatus) {
        this.guardaComentarioTemporal();
      } else {
        this.alertService().showWarning(this, this.$t('comentario-temporal.alert.sin-datos').toString());
      }
    } else {
      this.$v.comentarioTemporal.$touch();
    }
  }

  private guardaComentarioTemporal() {
    this.comentarioTemporalBase.registroEstatusId = this.$store.getters.idRegistroEstatus;

    // Recupera el texto completo del campo de comentario actual
    const comentariosActuales = this.comentarioTemporal.comentario || '';

    const comentarioExistente = this.comentarioExistente || '';

    // No hay cambios
    if (comentariosActuales === comentarioExistente) {
      this.alertService().showWarning(this, this.$t('comentario-temporal.alert.sin-cambios').toString());
      return;
    }

    // Identificamos la parte nueva del comentario
    const comentarioNuevo = comentariosActuales.replace(comentarioExistente, '').trim();

    // Si el comentario actual es más corto que el comentario existente, o si no hay comentario nuevo
    if (comentariosActuales.length < comentarioExistente.length || !comentarioNuevo) {
      // Reemplaza todo el contenido con lo que está en el campo de texto actualmente
      this.comentarioTemporalBase.comentario = comentariosActuales;
    } else if (comentarioNuevo) {
      // Si hay un comentario nuevo, lo concatenamos con el nombre de la pantalla
      const nuevoComentarioConPantalla = this.obtenerEquivalencia(this.routeName) + ' : ' + comentarioNuevo;

      // Concatenamos el nuevo comentario con lo existente
      this.comentarioTemporalBase.comentario = comentarioExistente
        ? comentarioExistente + '\n' + nuevoComentarioConPantalla
        : nuevoComentarioConPantalla;
    }

    // guardar si hay cambios en el comentario
    if (this.comentarioTemporalBase.comentario !== comentarioExistente) {
      this.reniecytApi()
        .guardarComentarioTemporal(this.comentarioTemporalBase)
        .then(res => {
          if (res.status == 204 || res.status == 201) {
            this.alertService().showSuccess(this, this.$t('comentario-temporal.alert.guardado').toString());
            this.toggleComentario();
            this.comentarioExistente = this.comentarioTemporalBase.comentario;
          }
        })
        .catch(err => {
          this.toggleComentario();
          if (err?.response?.data?.detail) {
            this.alertService().showError(this, err.response.data.detail);
          } else {
            this.alertService().showError(this, this.$t('comentario-temporal.alert.error-guardado').toString());
          }
        });
    }
  }

  private consultaComentarioTemporal() {
    this.reniecytApi()
      .getComentarioTemporalRegistro(this.$store.getters.idRegistroEstatus)
      .then(res => {
        if (res.status == 200) {
          this.comentarioTemporal.comentario = res.data.comentario || '';
          this.comentarioExistente = this.comentarioTemporalBase.comentario;
        }
      })
      .catch(err => {
        if (err.response?.status !== 404) {
          if (err?.response?.data?.detail) {
            this.alertService().showError(this, err.response.data.detail);
          } else {
            this.alertService().showError(this, this.$t('comentario-temporal.alert.error-consulta').toString());
          }
        }
      });
  }

  private obtenerEquivalencia(seccion) {
    const equivalencias = {
      SolicitudMoral: this.$t('secciones.solicitudMoral').toString(),
      DatosGenerales: this.$t('secciones.datosGenerales').toString(),
      DomicilioFiscal: this.$t('secciones.domicilioFiscal').toString(),
      RepresentanteSede: this.$t('secciones.representanteSede').toString(),
      DocumentoCreacion: this.$t('secciones.documentoCreacion').toString(),
      DocumentosMoral: this.$t('secciones.documentosMoral').toString(),
      Subsedes: this.$t('secciones.subsedes').toString(),
      SolicitudFisica: this.$t('secciones.solicitudFisica').toString(),
      DomicilioResidenciaFisica: this.$t('secciones.domicilioResidenciaFisica').toString(),
      AddPoderOtorgado: this.$t('secciones.addPoderOtorgado').toString(),
      MediosContactoFisica: this.$t('secciones.mediosContactoFisica').toString(),
      DocumentosFisica: this.$t('secciones.documentosFisica').toString(),
    };

    if (this.isRepresentante && this.isSubSede) {
      return (
        equivalencias[seccion] +
          '(' +
          this.$t('secciones.subsede').toString() +
          '-' +
          this.$t('secciones.representanteLegal').toString() +
          ')' || this.$t('secciones.noEncontrada').toString()
      );
    } else if (this.isSubSede) {
      return equivalencias[seccion] + '(' + this.$t('secciones.subsede').toString() + ')' || this.$t('secciones.noEncontrada').toString();
    } else if (this.isRepresentante) {
      return (
        equivalencias[seccion] + '(' + this.$t('secciones.representanteLegal').toString() + ')' ||
        this.$t('secciones.noEncontrada').toString()
      );
    } else {
      return equivalencias[seccion] || this.$t('secciones.noEncontrada').toString();
    }
  }
}
